import { Flex, Input, Text } from "@chakra-ui/react";
import { LoaderCircle, Paperclip } from "lucide-react";
import {
	forwardRef,
	useCallback,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import FileIconStl from "./icons/files/FileIconStl";
import TrashIcon from "./icons/TrashIcon";
import BrandFullCheckIcon from "./icons/BrandFullCheckIcon";
import { filesize } from "filesize";
import { useNewOrderStore } from "../stores/order.store";

export type TemporaryFile = {
	id?: number;
	name: string;
	size: number | string;
};

interface FileInlineInputProps {
	name: string;
	display?: string;
	fileName?: string;
	files?: TemporaryFile[];
	onFileChange?: (files: File[] | null) => void;
	onReset?: () => void;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	accept?: string;
	isUploading?: boolean;
}

const FileInlineInputPlaceholder = () => {
	return (
		<Flex direction={"row"} alignItems={"center"} gap={2} px={2}>
			<Paperclip size="20px" />
			<Text fontSize="14px" fontWeight={600}>
				Joindre les empreintes
			</Text>
		</Flex>
	);
};

const FileInlineInputPreview = ({
	fileName,
	fileSize,
	isUploading,
	onRemove,
}: {
	fileName: string;
	fileSize: number;
	isUploading: boolean;
	onRemove: () => void;
}) => {
	return (
		<div className="w-full grid grid-cols-[24px_1fr_40px] gap-2 px-2 items-center">
			<FileIconStl w="24px" h="24px" />
			<div className="grid grid-row-2">
				<div className="flex items-center gap-2">
					<Text fontSize="14px" fontStyle="normal" fontWeight={500}>
						{fileName}
					</Text>
					{!isUploading && (
						<BrandFullCheckIcon
							color="text-success-600"
							className="text-success-600"
						/>
					)}
				</div>
				<Text fontSize="14px" fontStyle="normal" fontWeight={300}>
					{typeof fileSize === "number" ? filesize(fileSize) : ""}
				</Text>
			</div>
			<div className="grid grid-cols-[16px_16px] gap-2">
				{isUploading ? (
					<LoaderCircle className="animate-spin w-4 h-4" />
				) : (
					<div />
				)}
				<TrashIcon
					className="text-error-700! cursor-pointer w-4 h-4"
					onClick={(event) => {
						event.stopPropagation();
						event.preventDefault();
						onRemove();
					}}
				/>
			</div>
		</div>
	);
};

export type FileInlineInputHandler = HTMLInputElement & {
	reset: () => void;
};

const FileInlineInput = forwardRef<
	FileInlineInputHandler,
	FileInlineInputProps
>(
	(
		{
			name,
			files,
			onFileChange,
			onReset,
			onChange,
			onBlur,
			display,
			accept,
			fileName = undefined,
			isUploading = false,
		},
		ref,
	) => {
		const { showError } = useNewOrderStore();
		const [file, setFile] = useState<File | TemporaryFile | null>(
			Array.isArray(files) && files.length > 0 ? files[0] : null,
		);

		const handleReset = useCallback(() => {
			const run = () => {
				setFile(null);
				if (inputRef.current) {
					inputRef.current.value = "";
				}
				if (onReset !== undefined) {
					onReset();
				}
			};
			// NOTE: A bit of a trick here
			setTimeout(run, 400);
		}, [onReset]);
		const handleOnInput = useCallback(() => {
			if (onFileChange) {
				if (inputRef.current?.files) {
					const currentFiles = Array.from(inputRef.current.files).slice(0, 1);
					if (currentFiles.length === 0 && file) {
						return;
					}
					onFileChange(currentFiles);
					setFile(currentFiles[0]);
				}
			}
		}, [onFileChange, file]);

		const inputRef = useRef<FileInlineInputHandler>(null);
		useImperativeHandle(ref, () => {
			const el = inputRef.current!;

			return Object.assign(el, {
				reset: handleReset,
			});
		}, [handleReset]);

		return (
			<Flex
				display={display}
				position="relative"
				bg="white"
				border="solid 1px"
				borderColor={showError && file == null ? "#FCA5A5" : "gray.200"}
				background={showError && file == null ? "#FEF2F2" : undefined}
				textColor={showError && file == null ? "#DC2626" : undefined}
				borderRadius="lg"
				justifyContent={"center"}
				direction={"column"}
				alignItems={file ? "flex-start" : "center"}
				h={file ? "48px" : "48px"}
				w={file ? "100%" : "fit-content"}
				onClick={() => {
					if (inputRef.current) {
						inputRef.current.click();
					}
				}}
				onDragOver={(event) => {
					event.stopPropagation();
					event.preventDefault();
				}}
				onDrop={(event) => {
					event.preventDefault();
					if (inputRef.current) {
						inputRef.current.files = event.dataTransfer.files;
						if (onFileChange) {
							onFileChange(Array.from(inputRef.current.files).slice(0, 1));
						}
					}
				}}
			>
				<Input
					ref={inputRef}
					type="file"
					name={name}
					display={"none"}
					onInput={handleOnInput}
					onChange={onChange}
					onBlur={onBlur}
					accept={accept}
				></Input>
				{file == null && <FileInlineInputPlaceholder />}
				{file != null && (
					<FileInlineInputPreview
						fileName={fileName ?? file.name}
						fileSize={Number.parseInt(file.size.toString(), 10)}
						isUploading={isUploading}
						onRemove={handleReset}
					/>
				)}
			</Flex>
		);
	},
);

export default FileInlineInput;
